import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth, authFetch } from "../App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  format, startOfMonth, endOfMonth, addMonths, subMonths,
  getISOWeek, getISOWeekYear, startOfISOWeek, endOfISOWeek, addWeeks, subWeeks, setISOWeek,
} from "date-fns";
import { de } from "date-fns/locale";
import { Plus, Trash2, ChevronLeft, ChevronRight, CalendarDays, CopyPlus, Search, UserCircle, Palmtree, X as XIcon, MapPin, Coffee, Download, FileText, FileSpreadsheet, EyeOff } from "lucide-react";
import StundenkontenDashboard from "./StundenkontenDashboard";
import { TodayOverview, WeekBarChart, MonthHeatmap } from "../components/StundenkontoVisuals";
import { GpsMiniMap } from "../modules/crm/GpsMiniMap";
import { useTranslation } from "../i18n/LanguageProvider";
import { getProjectTypeColor } from "../config/projectTypeColors";
import ResetDialog from "./stundenkonten/components/ResetDialog";
import VacationPlanModal from "./stundenkonten/components/VacationPlanModal";
import InlineProjectPicker from "./stundenkonten/components/InlineProjectPicker";
import HiddenColumnsButton from "./stundenkonten/components/HiddenColumnsButton";
import { useColumnPreferences } from "./stundenkonten/columnPrefs";
import {
  API, MONTHS, WD, STATUS_OPTS, PAUSE_OPTS, GRUND_OPTS,
  hm, toM, TODAY, mkRange, mkKey, mkPrev, mkNext,
  TH, thBorder, thGroupBorder, tdS, tdGS, plainSelect,
  COL_DEFS, LS_KEY, loadColWidths, saveColWidths,
} from "./stundenkonten/constants";

export default function Stundenkonten() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [monthsData, setMonthsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editField, setEditField] = useState(null);
  const [ev, setEv] = useState({});
  const [saving, setSaving] = useState(false);
  const [projPickerId, setProjPickerId] = useState(null);
  const projCloseRef = useRef(0);
  const pendingPickerDate = useRef(null);
  const [flashId, setFlashId] = useState(null);
  const [selDates, setSelDates] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkVals, setBulkVals] = useState({ proj: "", von: "08:00", bis: "16:30", pause: 30, status: "arbeitstag" });
  const [editVacation, setEditVacation] = useState(false);
  const [vacInput, setVacInput] = useState("");  const [resetFrom, setResetFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; });
  const [resetTo, setResetTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [vacSummary, setVacSummary] = useState(null);
  const [pendingVacationCount, setPendingVacationCount] = useState(0);
  const [showVacModal, setShowVacModal] = useState(false);
  const [gpsMapData, setGpsMapData] = useState(null);

  /* Column resize state */
  const [colWidths, setColWidths] = useState(() => {
    const saved = loadColWidths();
    const w = {};
    COL_DEFS.forEach(c => { w[c.key] = saved[c.key] || c.w; });
    return w;
  });
  const resizeRef = useRef(null);
  const getColW = (key) => colWidths[key] || COL_DEFS.find(c => c.key === key)?.w || 60;

  // Sticky columns: first 3 (wt, act, datum) — offsets stable via useMemo
  const STICKY_COUNT = 3;
  const stickyLefts = useMemo(() => {
    const arr = [];
    let acc = 0;
    for (let i = 0; i < STICKY_COUNT; i++) {
      arr[i] = acc;
      acc += colWidths[COL_DEFS[i]?.key] || COL_DEFS[i]?.w || 60;
    }
    return arr;
  }, [colWidths]);
  const getStickyStyle = (colIndex) => {
    if (colIndex >= STICKY_COUNT) return {};
    const isLast = colIndex === STICKY_COUNT - 1;
    return {
      position: "sticky",
      left: stickyLefts[colIndex],
      zIndex: 12,
      // BUGFIX 07.02.2026: kein "inherit" mehr — sonst scrollt Inhalt unter sticky cols durch.
      // Cell setzt selber backgroundColor:bg (deckend) ueber diesen default.
      backgroundColor: "var(--tga-bg-card)",
      // Visuelle Trennung an der letzten sticky-Kante
      boxShadow: isLast ? "4px 0 6px -2px rgba(0,0,0,0.35)" : undefined,
    };
  };
  const getStickyHeaderStyle = (colIndex) => {
    if (colIndex >= STICKY_COUNT) return {};
    const isLast = colIndex === STICKY_COUNT - 1;
    return {
      position: "sticky",
      left: stickyLefts[colIndex],
      zIndex: 26,
      backgroundColor: "var(--tga-bg-elevated)",
      boxShadow: isLast ? "4px 0 6px -2px rgba(0,0,0,0.35)" : undefined,
    };
  };
  const onResizeStart = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX; const startW = getColW(key);
    resizeRef.current = { key, startX, startW };
    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;inset:0;z-index:9999;cursor:col-resize;";
    overlay.id = "col-resize-overlay";
    document.body.appendChild(overlay);
    const onMove = (ev) => {
      const diff = ev.clientX - startX;
      const newW = Math.max(28, startW + diff);
      setColWidths(prev => { const n = { ...prev, [key]: newW }; return n; });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      overlay.remove();
      setColWidths(prev => { saveColWidths(prev); return prev; });
      resizeRef.current = null;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const now = new Date();
  const [kwDate, setKwDate] = useState(startOfISOWeek(now));
  const activeKw = getISOWeek(kwDate);
  const activeYear = getISOWeekYear(kwDate);
  const kwStart = startOfISOWeek(kwDate);
  const kwEnd = endOfISOWeek(kwDate);
  const kwDateRange = `${format(kwStart, "d. MMMM", { locale: de })} – ${format(kwEnd, "d. MMMM yyyy", { locale: de })}`;
  const visMonthKey = mkKey(kwStart);
  const kwStartStr = format(kwStart, "yyyy-MM-dd");

  const ldRef = useRef(new Set());
  const mdRef = useRef(monthsData);
  mdRef.current = monthsData;
  const bottomRef = useRef(null);
  const tableRef = useRef(null);

  const isAdmin = user?.role === "admin" || user?.role === "projektleiter";
  const isEditable = isAdmin && !!selectedUserId;
  const hasEmployee = !!selectedUserId;
  const sortedKeys = useMemo(() => Object.keys(monthsData).sort(), [monthsData]);

  // Reduced view logic: hide travel columns for Nachunternehmer or users without extended_time_tracking
  const selectedUser = useMemo(() => users.find(u => u.id === selectedUserId), [users, selectedUserId]);
  const isReducedView = selectedUser?.role === "nachunternehmer" || selectedUser?.extended_time_tracking === false;
  const isNachunternehmer = selectedUser?.role === "nachunternehmer";
  const TRAVEL_KEYS = useMemo(() => ["fhkm", "fhz", "gh", "frkm", "frz", "gr"], []);
  const NU_HIDDEN_KEYS = useMemo(() => new Set([...TRAVEL_KEYS, "soll", "diff"]), [TRAVEL_KEYS]);

  // Responsive viewport width (auto-collapse less-important columns on small screens)
  // Debounced to prevent layout-loop-induced jitter during toggles
  const [viewportW, setViewportW] = useState(typeof window !== "undefined" ? window.innerWidth : 1920);
  useEffect(() => {
    let t;
    const onR = () => { clearTimeout(t); t = setTimeout(() => setViewportW(window.innerWidth), 150); };
    window.addEventListener("resize", onR);
    return () => { clearTimeout(t); window.removeEventListener("resize", onR); };
  }, []);

  // Column preferences (per-user localStorage)
  const roleDefaults = useMemo(() => ({
    hiddenKeys: isNachunternehmer
      ? [...NU_HIDDEN_KEYS]
      : (isReducedView ? [...TRAVEL_KEYS] : []),
  }), [isNachunternehmer, isReducedView, NU_HIDDEN_KEYS, TRAVEL_KEYS]);

  const ALL_KEYS = useMemo(() => COL_DEFS.map(c => c.key), []);
  const prefs = useColumnPreferences({
    userId: user?.id,
    allKeys: ALL_KEYS,
    travelKeys: TRAVEL_KEYS,
    roleDefaults,
  });

  // DOM-STABLE STRATEGY: always render ALL columns. Hide via CSS (display: none).
  // This eliminates mount/unmount flicker when toggling columns.
  const LOCKED_KEYS = useMemo(() => new Set(["wt", "act", "datum", "status", "projekt"]), []);

  const hiddenKeys = useMemo(() => {
    const hidden = new Set();
    for (const c of COL_DEFS) {
      if (LOCKED_KEYS.has(c.key)) continue; // sticky/locked cols always visible
      // 1) Role lock (Nachunternehmer → Fahrt/Soll/+- hart aus)
      if (isNachunternehmer && NU_HIDDEN_KEYS.has(c.key)) { hidden.add(c.key); continue; }
      // 2) User preference
      if (!prefs.visible.has(c.key)) { hidden.add(c.key); continue; }
      // 3) Responsive auto-hide
      if (viewportW < 1280 && TRAVEL_KEYS.includes(c.key)) { hidden.add(c.key); continue; }
      if (viewportW < 900 && (c.key === "pause" || c.key === "soll")) { hidden.add(c.key); continue; }
    }
    return hidden;
  }, [prefs.visible, viewportW, isNachunternehmer, NU_HIDDEN_KEYS, TRAVEL_KEYS, LOCKED_KEYS]);

  // All columns are rendered; activeCols is now the STABLE set (same order always)
  const activeCols = COL_DEFS;
  const COL_COUNT = activeCols.length;

  // Compact mode heights for cells
  const rowH = prefs.compact ? 28 : 38;

  // Stable minimum table width (uses VISIBLE columns only — content-shrink is fine via CSS)
  const tableMinWidth = useMemo(
    () => activeCols.reduce((s, c) => hiddenKeys.has(c.key) ? s : s + (colWidths[c.key] || c.w || 60), 0) + 20,
    [activeCols, colWidths, hiddenKeys]
  );

  // Preserve horizontal scroll position across column toggles
  const scrollLeftRef = useRef(0);
  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    const onScroll = () => { scrollLeftRef.current = el.scrollLeft; };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const el = tableRef.current;
    if (el && scrollLeftRef.current) {
      requestAnimationFrame(() => { el.scrollLeft = scrollLeftRef.current; });
    }
  }, [hiddenKeys, prefs.compact]);

  // Derived flags — all based on stable COL_DEFS index
  const showTravel = useMemo(
    () => TRAVEL_KEYS.some(k => !hiddenKeys.has(k)),
    [hiddenKeys, TRAVEL_KEYS]
  );

  // Helper for td/th className including "col-hidden" when hidden
  const colCls = (key) => hiddenKeys.has(key) ? "col-hidden" : "";

  useEffect(() => {
    // Mitarbeiter (non-admin) bekommen NUR ihre zugewiesenen Projekte (gleiche Logik wie Stempeln/Stundenzettel)
    // Admin/Projektleiter/Büro bekommen wie bisher alle Projekte
    const projectsUrl = isAdmin
      ? `${API}/projects`
      : `${API}/projects?statuses=aktiv,in_planung&for_timesheet=true`;
    Promise.all([authFetch(`${API}/users`), authFetch(projectsUrl)]).then(async ([u, p]) => {
      if (u.ok) {
        const userList = await u.json();
        setUsers(Array.isArray(userList) ? userList : []);
        // URL-Param 'user' (von Einsatzplanung-Klick) hat Vorrang — Auto-Select dieses Users
        const urlUser = searchParams.get("user");
        if (urlUser && Array.isArray(userList) && userList.find(x => x.id === urlUser)) {
          setSelectedUserId(urlUser);
        } else if (!isAdmin && Array.isArray(userList) && userList.length >= 1) {
          // Non-admin: always auto-select own user (backend returns only self)
          setSelectedUserId(userList[0].id);
        }
      }
      if (p.ok) {
        try {
          const projList = await p.json();
          // Defensive: nur valide Projekte mit id + name behalten
          const safe = Array.isArray(projList)
            ? projList.filter(x => x && typeof x === "object" && x.id && (x.name || x.short_code))
            : [];
          setProjects(safe);
        } catch {
          setProjects([]);
        }
      } else {
        setProjects([]);
      }
    }).catch(() => {
      // Niemals crashen – immer mit leeren Arrays starten
      setUsers([]);
      setProjects([]);
    });
    // URL-Param 'date' (von Einsatzplanung-Klick) — auf KW dieses Datums springen
    const urlDate = searchParams.get("date");
    if (urlDate && /^\d{4}-\d{2}-\d{2}$/.test(urlDate)) {
      const d = new Date(urlDate + "T12:00:00");
      if (!isNaN(d.getTime())) {
        setKwDate(startOfISOWeek(d));
        didScrollRef.current = false; // reset auto-scroll so it re-runs to new KW
      }
    }
  }, [isAdmin, searchParams]);

  // Load pending vacation request count (shown as badge on "Urlaubsverwaltung" button)
  const loadPendingVacations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedUserId) params.set("employee_id", selectedUserId);
      const res = await authFetch(`${API}/vacation/requests?${params}`);
      if (res.ok) {
        const data = await res.json();
        setPendingVacationCount(data.filter(r => r.status === "geplant").length);
      }
    } catch { /* ignore */ }
  }, [selectedUserId]);

  useEffect(() => { loadPendingVacations(); }, [loadPendingVacations]);

  const fetchMo = useCallback(async (key) => {
    if (!selectedUserId || mdRef.current[key] || ldRef.current.has(key)) return;
    ldRef.current.add(key);
    try {
      const { from, to } = mkRange(key);
      const res = await authFetch(`${API}/timesheet/summary?from=${from}&to=${to}&employee_id=${selectedUserId}`);
      if (res.ok) {
        const data = await res.json();
        setMonthsData(prev => { const n = { ...prev, [key]: data }; mdRef.current = n; return n; });
      }
    } finally { ldRef.current.delete(key); }
  }, [selectedUserId]);

  // Load ALL 12 months of a year in parallel
  const loadFullYear = useCallback(async (year) => {
    if (!selectedUserId) return;
    const keys = [];
    for (let m = 1; m <= 12; m++) keys.push(`${year}-${String(m).padStart(2, "0")}`);
    const fetches = keys.filter(k => !mdRef.current[k] && !ldRef.current.has(k)).map(async (key) => {
      ldRef.current.add(key);
      try {
        const { from, to } = mkRange(key);
        const res = await authFetch(`${API}/timesheet/summary?from=${from}&to=${to}&employee_id=${selectedUserId}`);
        if (res.ok) return [key, await res.json()];
      } finally { ldRef.current.delete(key); }
      return null;
    });
    const results = await Promise.all(fetches);
    const newData = {};
    results.forEach(r => { if (r) newData[r[0]] = r[1]; });
    if (Object.keys(newData).length > 0) {
      setMonthsData(prev => { const n = { ...prev, ...newData }; mdRef.current = n; return n; });
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (!selectedUserId) { setMonthsData({}); mdRef.current = {}; setSelDates(new Set()); setBulkAction(null); return; }
    setMonthsData({}); mdRef.current = {}; ldRef.current.clear(); setLoading(true); setSelDates(new Set()); setBulkAction(null);
    (async () => {
      await loadFullYear(activeYear);
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  // On year change, load the new year
  const prevYearRef = useRef(activeYear);
  useEffect(() => {
    if (!selectedUserId || activeYear === prevYearRef.current) return;
    prevYearRef.current = activeYear;
    setSelDates(new Set()); setBulkAction(null);
    setLoading(true);
    (async () => {
      await loadFullYear(activeYear);
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeYear, selectedUserId]);

  // On kwDate change, just clear selection (data is already loaded)
  useEffect(() => {
    if (!selectedUserId) return;
    setSelDates(new Set()); setBulkAction(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kwDate]);

  // Vacation summary fetch
  useEffect(() => {
    if (!selectedUserId) { setVacSummary(null); return; }
    (async () => {
      try {
        const res = await authFetch(`${API}/vacation/extended-summary/${selectedUserId}/${activeYear}`);
        if (res.ok) setVacSummary(await res.json());
        else setVacSummary(null);
      } catch { setVacSummary(null); }
    })();
  }, [selectedUserId, activeYear]);

  // Auto-refresh current month data every 90s
  useEffect(() => {
    if (!selectedUserId) return;
    const interval = setInterval(() => {
      const mk = kwDate.toISOString().substring(0, 7);
      ldRef.current.delete(mk);
      const { from, to } = mkRange(mk);
      authFetch(`${API}/timesheet/summary?from=${from}&to=${to}&employee_id=${selectedUserId}`)
        .then(async (res) => {
          if (res.ok) {
            const data = await res.json();
            setMonthsData(prev => { const n = { ...prev, [mk]: data }; mdRef.current = n; return n; });
          }
        }).catch(() => {});
    }, 90000);
    return () => clearInterval(interval);
  }, [selectedUserId, kwDate]);

  const reloadMo = useCallback(async (date) => {
    const k = date.substring(0, 7);
    const scrollTop = window.scrollY;
    ldRef.current.delete(k);
    try {
      const { from, to } = mkRange(k);
      const res = await authFetch(`${API}/timesheet/summary?from=${from}&to=${to}&employee_id=${selectedUserId}`);
      if (res.ok) {
        const data = await res.json();
        setMonthsData(prev => { const n = { ...prev, [k]: data }; mdRef.current = n; return n; });
      }
    } catch {}
    requestAnimationFrame(() => { window.scrollTo(0, scrollTop); });
  }, [selectedUserId]);

  /* Optimistic local update – patches one entry in monthsData without server refetch */
  const patchEntryLocal = useCallback((entryId, date, patches) => {
    const k = date.substring(0, 7);
    setMonthsData(prev => {
      const md = prev[k]; if (!md) return prev;
      const newDays = md.days.map(day => {
        if (day.date !== date) return day;
        return { ...day, entries: day.entries.map(e => e.id === entryId ? { ...e, ...patches } : e) };
      });
      const n = { ...prev, [k]: { ...md, days: newDays } }; mdRef.current = n; return n;
    });
    // Flash feedback
    setFlashId(entryId);
    setTimeout(() => setFlashId(null), 300);
  }, []);

  const cd = monthsData[visMonthKey];
  const sm = cd?.summary || {};
  const vac = cd?.vacation_summary;
  const stats = useMemo(() => {
    const days = cd?.days || [];
    return {
      at: days.filter(d => !d.is_weekend && !d.is_holiday).length,
      urlaub: days.filter(d => d.absence_type === "urlaub").length,
      krank: days.filter(d => d.absence_type === "krank").length,
    };
  }, [cd]);

  const recentProjectIds = useMemo(() => {
    const ids = []; const seen = new Set();
    const allDays = [];
    sortedKeys.forEach(k => { if (monthsData[k]?.days) allDays.push(...monthsData[k].days); });
    allDays.sort((a, b) => b.date.localeCompare(a.date));
    for (const day of allDays) {
      for (const e of (day.entries || [])) {
        if (e.project_id && !seen.has(e.project_id)) { seen.add(e.project_id); ids.push(e.project_id); }
        if (ids.length >= 5) return ids;
      }
    }
    return ids;
  }, [sortedKeys, monthsData]);

  /* Build flat row array */
  const rows = useMemo(() => {
    if (!sortedKeys.length) return [];
    const allDays = [];
    sortedKeys.forEach(k => { if (monthsData[k]?.days) allDays.push(...monthsData[k].days); });
    allDays.sort((a, b) => a.date.localeCompare(b.date));
    const seen = new Set();
    const days = allDays.filter(d => { if (seen.has(d.date)) return false; seen.add(d.date); return true; });
    const res = [];
    let curKw = null;
    let prevDayEntry = null;
    for (const day of days) {
      const d = new Date(day.date);
      const kw = getISOWeek(d);
      const kwK = `${d.getFullYear()}-${kw}`;
      const wd = WD[day.weekday] || day.weekday?.slice(0, 2) || "";
      const ds = format(d, "dd.MM.yyyy");
      const isWe = day.is_weekend, isHol = day.is_holiday;
      const holName = day.holiday_name || null;
      const isAbs = day.absence_type && day.absence_type !== "arbeitstag";
      const absT = isWe && (!day.absence_type || day.absence_type === "arbeitstag") ? "weekend" : day.absence_type || "arbeitstag";
      const vacStatus = day.vacation_status || null;
      const sLbl = (isHol || absT === "feiertag") ? "Feiertag" : absT === "urlaub" ? (vacStatus === "geplant" ? "Urlaub (geplant)" : "Urlaub") : isAbs ? (STATUS_OPTS.find(s => s.value === day.absence_type)?.label || day.absence_type) : isWe ? "Wochenende" : "Arbeitstag";
      const sollM = day.soll_minutes || 0;
      const dayIst = day.ist_minutes || 0;
      const isAutoIst = ["feiertag", "krank", "urlaub"].includes(absT);
      if (kwK !== curKw) {
        if (curKw) {
          const wr = res.filter(r => r.kwK === curKw && r.type === "entry");
          const wIst = wr.filter(r => r.first).reduce((s, r) => s + (r.isAutoIst ? r.dayIst : wr.filter(rr => rr.date === r.date).reduce((ss, rr) => ss + rr.az, 0)), 0);
          const wSoll = wr.filter(r => r.first).reduce((s, r) => s + r.soll, 0);
          const wFahrt = wr.reduce((s, r) => s + r.fzH + r.fzR, 0);
          res.push({ type: "wk-sum", kwK: curKw, kw: wr[0]?.kw, wIst, wSoll, wFahrt });
        }
        curKw = kwK;
        res.push({ type: "kw", kwK, kw, label: `KW ${kw}` });
      }
      const entries = day.entries || [];
      const dayTrips = day.trips || [];
      const prevEntry = prevDayEntry ? { pId: prevDayEntry.pId, proj: prevDayEntry.proj, von: prevDayEntry.von, bis: prevDayEntry.bis, pause: prevDayEntry.pause } : null;
      if (entries.length === 0) {
        const autoProj = absT === "feiertag" ? (holName && holName !== "Feiertag" ? `Feiertag – ${holName}` : "Feiertag") : absT === "urlaub" ? "Urlaub" : absT === "krank" ? "Krank" : "";
        res.push({ type: "entry", kwK, kw, date: day.date, wd, ds, sLbl, absT, vacStatus, proj: autoProj, pId: "", von: "", bis: "", pause: 0, az: 0, kmH: 0, kmR: 0, fzH: 0, fzR: 0, gH: "", gHN: "", gR: "", gRN: "", soll: sollM, dayIst, isAutoIst, eId: null, isWe, isHol, holName, first: true, prev: prevEntry, entryCount: 0, clockInLoc: day.clock_in_location, clockOutLoc: day.clock_out_location, locStatus: day.location_status, autoPauseApplied: day.auto_pause_applied, autoPauseMinutes: day.auto_pause_minutes, dayTrips });
      } else {
        entries.forEach((e, ei) => {
          const p = projects.find(pp => pp.id === e.project_id);
          const ws = e.work_start || "", we = e.work_end || "";
          const brk = e.work_break || e.break_minutes || 0;
          const wM = ws && we ? Math.max(0, toM(we) - toM(ws) - brk) : 0;
          const tOut = e.travel_outbound || {};
          const tRet = e.travel_return || {};
          const kH = tOut.kilometers || e.km_outbound || 0;
          const kR = tRet.kilometers || e.km_return || 0;
          const fzH = tOut.minutes || 0;
          const fzR = tRet.minutes || 0;
          const gH = tOut.reason || "";
          const gHN = tOut.reason_note || "";
          const gR = tRet.reason || "";
          const gRN = tRet.reason_note || "";
          const autoProj = absT === "feiertag" && ei === 0 ? (holName && holName !== "Feiertag" ? `Feiertag – ${holName}` : "Feiertag") : absT === "urlaub" && ei === 0 ? "Urlaub" : absT === "krank" && ei === 0 ? "Krank" : "";
          const rowData = { type: "entry", kwK, kw, date: day.date, wd: ei === 0 ? wd : "", ds: ei === 0 ? ds : "", sLbl: ei === 0 ? sLbl : "", absT, vacStatus, proj: p?.name || e.project_name || autoProj, pId: e.project_id || "", von: ws, bis: we, pause: brk, az: wM, kmH: kH, kmR: kR, fzH, fzR, gH, gHN, gR, gRN, tOut, tRet, soll: ei === 0 ? sollM : 0, dayIst, isAutoIst, eId: e.id, isWe, isHol, holName, first: ei === 0, prev: ei === 0 ? prevEntry : null, entryCount: entries.length, clockInLoc: ei === 0 ? day.clock_in_location : null, clockOutLoc: ei === 0 ? day.clock_out_location : null, locStatus: ei === 0 ? day.location_status : null, autoPauseApplied: ei === 0 ? day.auto_pause_applied : false, autoPauseMinutes: ei === 0 ? day.auto_pause_minutes : 0, dayTrips: ei === 0 ? dayTrips : [],
            // Abrechnungs-Felder (07.02.2026)
            vma: e.meal_allowance ?? 0,
            nacht: !!(e.overnight_stay || e.overnight),
            unterkunft: e.accommodation_location || "",
            abger: !!e.is_billed,
          };
          res.push(rowData);
          if (ei === 0) prevDayEntry = rowData;
        });
        if (entries.length > 1) {
          const dAz = entries.reduce((s, e) => { const ws = e.work_start || "", we = e.work_end || ""; const brk = e.work_break || e.break_minutes || 0; return s + (ws && we ? Math.max(0, toM(we) - toM(ws) - brk) : 0); }, 0);
          const dFahrt = entries.reduce((s, e) => s + ((e.travel_outbound?.minutes || 0) + (e.travel_return?.minutes || 0)), 0);
          res.push({ type: "dy-sum", date: day.date, kwK, dAz, dSoll: sollM, dFahrt });
        }
      }
      if (entries.length === 0 && !isWe && !isHol) prevDayEntry = null;
    }
    if (curKw) {
      const wr = res.filter(r => r.kwK === curKw && r.type === "entry");
      const wIst = wr.filter(r => r.first).reduce((s, r) => s + (r.isAutoIst ? r.dayIst : wr.filter(rr => rr.date === r.date).reduce((ss, rr) => ss + rr.az, 0)), 0);
      const wSoll = wr.filter(r => r.first).reduce((s, r) => s + r.soll, 0);
      const wFahrt = wr.reduce((s, r) => s + r.fzH + r.fzR, 0);
      res.push({ type: "wk-sum", kwK: curKw, kw: wr[0]?.kw, wIst, wSoll, wFahrt });
    }
    return res;
  }, [sortedKeys, monthsData, projects]);

  /* Auto-scroll to current week after data loads */
  const didScrollRef = useRef(false);
  useEffect(() => {
    if (!rows.length || loading || didScrollRef.current) return;
    didScrollRef.current = true;
    requestAnimationFrame(() => {
      // Wenn URL-Param 'date' gesetzt → exakt diese Zeile anvisieren + highlighten
      const urlDate = searchParams.get("date");
      if (urlDate) {
        const dayRow = document.getElementById(`row-${urlDate}`);
        if (dayRow && tableRef.current) {
          const containerTop = tableRef.current.getBoundingClientRect().top;
          const rowTop = dayRow.getBoundingClientRect().top;
          const theadH = tableRef.current.querySelector("thead")?.offsetHeight || 55;
          tableRef.current.scrollTop += (rowTop - containerTop - theadH - 4);
          // Kurzer Highlight-Pulse (CSS-Animation via inline style)
          dayRow.style.transition = "box-shadow 600ms ease, background-color 600ms ease";
          dayRow.style.boxShadow = "inset 0 0 0 2px var(--tga-blue, #3b82f6)";
          setTimeout(() => {
            dayRow.style.boxShadow = "";
          }, 1800);
          return;
        }
      }
      const kwRow = document.querySelector(`[data-kw="${activeYear}-${activeKw}"]`);
      if (kwRow && tableRef.current) {
        const containerTop = tableRef.current.getBoundingClientRect().top;
        const rowTop = kwRow.getBoundingClientRect().top;
        const theadH = tableRef.current.querySelector("thead")?.offsetHeight || 55;
        tableRef.current.scrollTop += (rowTop - containerTop - theadH);
      }
    });
  }, [rows.length, loading]);

  /* Auto-open picker after entry creation */
  useEffect(() => {
    if (!pendingPickerDate.current) return;
    const d = pendingPickerDate.current;
    pendingPickerDate.current = null;
    const entry = rows.find(r => r.type === "entry" && r.date === d && r.eId);
    if (entry) setProjPickerId(entry.eId);
  }, [rows]);

  /* Cell edit handlers */
  const startCellEdit = (row, field) => {
    if (!row.eId || !isEditable) return;
    if (editId === row.eId && editField === field) return;
    if (editId && editId !== row.eId) saveCellEdit(rows.find(r => r.eId === editId));
    setEditId(row.eId); setEditField(field);
    setEv({ von: row.von || "08:00", bis: row.bis || "16:30", pause: row.pause || 30, pId: row.pId, kmH: row.kmH, kmR: row.kmR, fzH: row.fzH, fzR: row.fzR, gH: row.gH, gHN: row.gHN, gR: row.gR, gRN: row.gRN, projText: row.proj || "" });
  };
  const cancelCellEdit = () => { setEditId(null); setEditField(null); setEv({}); };
  const saveCellEdit = async (row) => {
    if (!row?.eId || saving) return;
    setSaving(true);
    try {
      const ch = [];
      if (ev.von !== undefined && ev.von !== row.von) ch.push(["work_start", ev.von]);
      if (ev.bis !== undefined && ev.bis !== row.bis) ch.push(["work_end", ev.bis]);
      if (ev.pause !== undefined && Number(ev.pause) !== row.pause) ch.push(["work_break", Number(ev.pause)]);
      if (ev.pId !== undefined && ev.pId !== row.pId) ch.push(["project_id", ev.pId]);
      if (ev.kmH !== undefined && Number(ev.kmH) !== row.kmH) ch.push(["km_outbound", Number(ev.kmH) || 0]);
      if (ev.kmR !== undefined && Number(ev.kmR) !== row.kmR) ch.push(["km_return", Number(ev.kmR) || 0]);
      if (ev.fzH !== undefined && Number(ev.fzH) !== row.fzH) ch.push(["travel_minutes_outbound", Number(ev.fzH) || 0]);
      if (ev.fzR !== undefined && Number(ev.fzR) !== row.fzR) ch.push(["travel_minutes_return", Number(ev.fzR) || 0]);
      if (ev.gH !== undefined && ev.gH !== row.gH) ch.push(["travel_reason_outbound", ev.gH]);
      if (ev.gHN !== undefined && ev.gHN !== row.gHN) ch.push(["travel_reason_outbound_note", ev.gHN]);
      if (ev.gR !== undefined && ev.gR !== row.gR) ch.push(["travel_reason_return", ev.gR]);
      if (ev.gRN !== undefined && ev.gRN !== row.gRN) ch.push(["travel_reason_return_note", ev.gRN]);
      if (ev.projText !== undefined && ev.projText !== row.proj) ch.push(["project_name", ev.projText]);
      if (ch.length) {
        // Optimistic local update
        const patches = {};
        for (const [f, v] of ch) {
          if (f === "work_start") patches.work_start = v;
          else if (f === "work_end") patches.work_end = v;
          else if (f === "work_break") patches.work_break = v;
          else if (f === "project_id") { patches.project_id = v; patches.project_name = projects.find(p => p.id === v)?.name || ""; }
          else if (f === "km_outbound") patches.km_outbound = v;
          else if (f === "km_return") patches.km_return = v;
          else if (f === "travel_minutes_outbound") patches.travel_outbound = { ...(row.tOut || {}), minutes: v };
          else if (f === "travel_minutes_return") patches.travel_return = { ...(row.tRet || {}), minutes: v };
          else if (f === "travel_reason_outbound") patches.travel_outbound = { ...(patches.travel_outbound || row.tOut || {}), reason: v };
          else if (f === "travel_reason_outbound_note") patches.travel_outbound = { ...(patches.travel_outbound || row.tOut || {}), reason_note: v };
          else if (f === "travel_reason_return") patches.travel_return = { ...(patches.travel_return || row.tRet || {}), reason: v };
          else if (f === "travel_reason_return_note") patches.travel_return = { ...(patches.travel_return || row.tRet || {}), reason_note: v };
          else if (f === "project_name") patches.project_name = v;
        }
        patchEntryLocal(row.eId, row.date, patches);
        // Send to server in background (no await on reloadMo)
        await Promise.all(ch.map(([f, v]) => authFetch(`${API}/time-entries/${row.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: f, value: v }) })));
        toast.success("Gespeichert");
      }
      setEditId(null); setEditField(null); setEv({});
    } catch { toast.error("Fehler"); } finally { setSaving(false); }
  };
  const onCellKey = (e, row) => {
    if (e.key === "Enter") { e.preventDefault(); saveCellEdit(row); }
    if (e.key === "Escape") { e.preventDefault(); cancelCellEdit(); }
  };

  // Quick patch for billing-related single fields (07.02.2026)
  const quickPatchEntry = useCallback(async (eId, date, field, value, localKey) => {
    if (!eId || !isEditable) return;
    if (localKey) patchEntryLocal(eId, date, { [localKey]: value });
    try {
      await authFetch(`${API}/time-entries/${eId}/admin-edit`, {
        method: "PATCH",
        body: JSON.stringify({ field, value }),
      });
    } catch { toast.error("Fehler beim Speichern"); }
  }, [isEditable, patchEntryLocal]);
  const updateStatus = async (date, absType) => {
    if (!isEditable) return; setSaving(true);
    try {
      const res = await authFetch(`${API}/timesheet/day/${date}/status?user_id=${selectedUserId}`, { method: "PATCH", body: JSON.stringify({ absence_type: absType }) });
      const resData = await res.json().catch(() => ({}));

      if (absType === "feiertag") {
        // Auto-set project_name to "Feiertag – {name}" on entries
        const holLabel = resData.holiday_name && resData.holiday_name !== "Feiertag" ? `Feiertag – ${resData.holiday_name}` : "Feiertag";
        const dayRows = rows.filter(r => r.type === "entry" && r.date === date && r.eId);
        if (dayRows.length > 0) {
          for (const r of dayRows) {
            if (r.pId) await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_id", value: "" }) });
            await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_name", value: holLabel }) });
          }
        } else {
          // Create entry with holiday project_name
          const cr = await authFetch(`${API}/timesheet/day/add-project?date=${date}&user_id=${selectedUserId}`, {
            method: "POST", body: JSON.stringify({ project_id: "", work_minutes: 480, work_start: "08:00", work_end: "16:30", work_break: 30, travel_minutes_outbound: 0, travel_minutes_return: 0, km_outbound: 0, km_return: 0, note: "" })
          });
          if (cr.ok) {
            const crData = await cr.json().catch(() => ({}));
            const newId = crData.entry?.id || crData.id || crData.entry_id;
            if (newId) await authFetch(`${API}/time-entries/${newId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_name", value: holLabel }) });
          }
        }
      } else if (absType === "urlaub" || absType === "krank") {
        // Auto-set project_name to "Urlaub" or "Krank"
        const label = absType === "urlaub" ? "Urlaub" : "Krank";
        const dayRows = rows.filter(r => r.type === "entry" && r.date === date && r.eId);
        if (dayRows.length > 0) {
          for (const r of dayRows) {
            if (r.pId) await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_id", value: "" }) });
            await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_name", value: label }) });
          }
        } else {
          const cr = await authFetch(`${API}/timesheet/day/add-project?date=${date}&user_id=${selectedUserId}`, {
            method: "POST", body: JSON.stringify({ project_id: "", work_minutes: 480, work_start: "08:00", work_end: "16:30", work_break: 30, travel_minutes_outbound: 0, travel_minutes_return: 0, km_outbound: 0, km_return: 0, note: "" })
          });
          if (cr.ok) {
            const crData = await cr.json().catch(() => ({}));
            const newId = crData.entry?.id || crData.id || crData.entry_id;
            if (newId) await authFetch(`${API}/time-entries/${newId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_name", value: label }) });
          }
        }
      } else if (absType === "frei") {
        // Frei: clear project
        const dayRows = rows.filter(r => r.type === "entry" && r.date === date && r.eId && r.pId);
        for (const r of dayRows) await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_id", value: "" }) });
      } else {
        // Changing to Arbeitstag: clear project_name only if it's auto-generated text
        const dayRows = rows.filter(r => r.type === "entry" && r.date === date && r.eId);
        for (const r of dayRows) {
          if (r.proj && (r.proj.startsWith("Feiertag") || r.proj === "Urlaub" || r.proj === "Krank")) {
            await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_name", value: "" }) });
          }
        }
      }
      await reloadMo(date);
    } finally { setSaving(false); }
  };
  const addEntry = async (date, openPicker) => {
    if (!isEditable) return; setSaving(true);
    try {
      const r = await authFetch(`${API}/timesheet/day/add-project?date=${date || TODAY}&user_id=${selectedUserId}`, {
        method: "POST", body: JSON.stringify({ project_id: "", work_minutes: 480, work_start: "08:00", work_end: "16:30", work_break: 30, travel_minutes_outbound: 0, travel_minutes_return: 0, km_outbound: 0, km_return: 0, note: "" })
      });
      if (r.ok) {
        if (openPicker) pendingPickerDate.current = date || TODAY;
        await reloadMo(date || TODAY);
        toast.success("Eintrag erstellt");
      }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Fehler"); }
    } catch { toast.error("Fehler"); } finally { setSaving(false); }
  };
  const delEntry = async (id, date) => {
    if (!confirm("Eintrag löschen?")) return; setSaving(true);
    try {
      // Check if this is a feiertag row before deleting
      const row = rows.find(r => r.eId === id);
      const isFeiertag = row && (row.absT === "feiertag" || row.absT === "urlaub" || row.absT === "krank" || row.isHol);
      const r = await authFetch(`${API}/time-entries/${id}`, { method: "DELETE" });
      if (r.ok) {
        // Reset day status to arbeitstag if deleting a feiertag entry
        if (isFeiertag) {
          await authFetch(`${API}/timesheet/day/${date}/status?user_id=${selectedUserId}`, { method: "PATCH", body: JSON.stringify({ absence_type: "arbeitstag" }) });
        }
        await reloadMo(date); toast.success("Gelöscht");
      }
    } catch { toast.error("Fehler"); } finally { setSaving(false); }
  };
  const setProjectDirect = async (entryId, projectId, date) => {
    if (!isEditable || saving) return; setSaving(true);
    try {
      const proj = projects.find(p => p.id === projectId);
      patchEntryLocal(entryId, date, { project_id: projectId, project_name: proj?.name || "" });
      setProjPickerId(null);
      await authFetch(`${API}/time-entries/${entryId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_id", value: projectId }) });
      toast.success("Projekt geändert");
    } catch { toast.error("Fehler"); } finally { setSaving(false); }
  };
  const copyPrevDay = async (row) => {
    if (!row.prev || !isEditable || saving) return; setSaving(true);
    try {
      const r = await authFetch(`${API}/timesheet/day/add-project?date=${row.date}&user_id=${selectedUserId}`, {
        method: "POST", body: JSON.stringify({ project_id: row.prev.pId || projects[0]?.id || "", work_minutes: 480, work_start: row.prev.von || "08:00", work_end: row.prev.bis || "16:30", work_break: row.prev.pause || 30, travel_minutes_outbound: 0, travel_minutes_return: 0, km_outbound: 0, km_return: 0, note: "" })
      });
      if (r.ok) { await reloadMo(row.date); toast.success("Vortag übernommen"); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Fehler"); }
    } catch { toast.error("Fehler"); } finally { setSaving(false); }
  };

  /* Multi-select */
  const handleRowSelect = (date) => {
    if (!isEditable || !date) return; cancelCellEdit();
    setSelDates(prev => { const next = new Set(prev); if (next.has(date)) next.delete(date); else next.add(date); return next; });
  };
  const clearSelection = () => { setSelDates(new Set()); setBulkAction(null); };
  const clearSelAndEdit = (fn) => { if (selDates.size) clearSelection(); if (fn) fn(); };

  const applyBulk = async (action) => {
    if (!selDates.size || saving) return; setSaving(true);
    try {
      const dates = [...selDates].sort(); let count = 0;
      for (const date of dates) {
        const dayRows = rows.filter(r => r.type === "entry" && r.date === date);
        const hasEntry = dayRows.some(r => r.eId);
        if (action === "status") {
          await authFetch(`${API}/timesheet/day/${date}/status?user_id=${selectedUserId}`, { method: "PATCH", body: JSON.stringify({ absence_type: bulkVals.status }) }); count++;
        } else if (action === "projekt" || action === "zeit") {
          const dayRow = rows.find(r => r.type === "entry" && r.date === date && r.first);
          if (action === "projekt" && dayRow && dayRow.absT !== "arbeitstag") continue;
          if (!hasEntry) {
            const pId = action === "projekt" ? bulkVals.proj : "";
            const von = action === "zeit" ? bulkVals.von : "08:00";
            const bis = action === "zeit" ? bulkVals.bis : "16:30";
            const pause = action === "zeit" ? bulkVals.pause : 30;
            await authFetch(`${API}/timesheet/day/add-project?date=${date}&user_id=${selectedUserId}`, {
              method: "POST", body: JSON.stringify({ project_id: pId, work_minutes: 480, work_start: von, work_end: bis, work_break: pause, travel_minutes_outbound: 0, travel_minutes_return: 0, km_outbound: 0, km_return: 0, note: "" })
            }); count++;
          } else {
            for (const r of dayRows) {
              if (!r.eId) continue;
              if (action === "projekt" && bulkVals.proj) await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "project_id", value: bulkVals.proj }) });
              if (action === "zeit") {
                await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "work_start", value: bulkVals.von }) });
                await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "work_end", value: bulkVals.bis }) });
                await authFetch(`${API}/time-entries/${r.eId}/admin-edit`, { method: "PATCH", body: JSON.stringify({ field: "work_break", value: Number(bulkVals.pause) }) });
              }
              count++;
            }
          }
        }
      }
      const months = new Set(dates.map(d => d.substring(0, 7)));
      const scrollTop = window.scrollY;
      for (const m of months) { ldRef.current.delete(m); await reloadMo(dates.find(d => d.startsWith(m)) || `${m}-01`); }
      requestAnimationFrame(() => { window.scrollTo(0, scrollTop); });
      clearSelection(); toast.success(`${count} ${count === 1 ? "Tag" : "Tage"} aktualisiert`);
    } catch { toast.error("Fehler beim Speichern"); } finally { setSaving(false); }
  };

  const bulkDelete = async () => {
    if (!selDates.size || saving) return;
    const datesWithEntries = [...selDates].filter(date => rows.some(r => r.type === "entry" && r.date === date && r.eId));
    if (!datesWithEntries.length) { toast.info("Keine Einträge zum Löschen vorhanden"); return; }
    if (!confirm(`Einträge für ${datesWithEntries.length} ausgewählte ${datesWithEntries.length === 1 ? "Tag" : "Tage"} wirklich löschen?`)) return;
    setSaving(true);
    try {
      let count = 0;
      for (const date of datesWithEntries) {
        const dayEntries = rows.filter(r => r.type === "entry" && r.date === date && r.eId);
        for (const r of dayEntries) { const res = await authFetch(`${API}/time-entries/${r.eId}`, { method: "DELETE" }); if (res.ok) count++; }
      }
      const months = new Set(datesWithEntries.map(d => d.substring(0, 7)));
      const scrollTop = window.scrollY;
      for (const m of months) { ldRef.current.delete(m); await reloadMo(datesWithEntries.find(d => d.startsWith(m)) || `${m}-01`); }
      requestAnimationFrame(() => { window.scrollTo(0, scrollTop); });
      clearSelection();
      toast.success(`${count} ${count === 1 ? "Eintrag" : "Einträge"} gelöscht`);
    } catch { toast.error("Fehler beim Löschen"); } finally { setSaving(false); }
  };

  const openResetDialog = () => {
    const d = new Date();
    setResetFrom(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`);
    setResetTo(d.toISOString().slice(0, 10));
    setShowResetDialog(true);
  };

  const resetAccount = async () => {
    if (!selectedUserId || saving || !resetFrom || !resetTo) return;
    setSaving(true); setShowResetDialog(false);
    try {
      const res = await authFetch(`${API}/timesheet/reset?user_id=${selectedUserId}&from_date=${resetFrom}&to_date=${resetTo}`, { method: "DELETE" });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setMonthsData({}); mdRef.current = {}; ldRef.current.clear();
        clearSelection();
        toast.success(`Stundenkonto im gewählten Zeitraum wurde erfolgreich zurückgesetzt. ${data.deleted_count || 0} Einträge gelöscht.`);
        await loadFullYear(activeYear);
      } else { toast.error("Fehler beim Zurücksetzen"); }
    } catch { toast.error("Fehler beim Zurücksetzen"); } finally { setSaving(false); }
  };


  useEffect(() => {
    if (!selDates.size) return;
    const handler = (e) => { if (e.key === "Escape") clearSelection(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selDates.size]);

  const saveVacationDays = async (val) => {
    setEditVacation(false);
    const num = parseInt(val, 10);
    if (isNaN(num) || num < 0 || !selectedUserId) return;
    try {
      await authFetch(`${API}/vacation/year-config/${selectedUserId}/${activeYear}`, { method: "PUT", body: JSON.stringify({ annual_entitlement: num }) });
      // Clear cached data to force refetch with updated vacation_summary
      setMonthsData({}); mdRef.current = {}; ldRef.current.clear();
      await loadFullYear(activeYear);
      toast.success(`Vertragliche Urlaubstage auf ${num} gesetzt`);
    } catch { toast.error("Fehler beim Speichern"); }
  };


  const scrollToKw = useCallback((year, kw) => {
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-kw="${year}-${kw}"]`);
      if (el && tableRef.current) {
        const containerTop = tableRef.current.getBoundingClientRect().top;
        const rowTop = el.getBoundingClientRect().top;
        const theadH = tableRef.current.querySelector("thead")?.offsetHeight || 55;
        tableRef.current.scrollTop += (rowTop - containerTop - theadH);
      }
    });
  }, []);

  const goKwPrev = () => { clearSelection(); setKwDate(d => { const nd = startOfISOWeek(subWeeks(d, 1)); scrollToKw(getISOWeekYear(nd), getISOWeek(nd)); return nd; }); };
  const goKwNext = () => { clearSelection(); setKwDate(d => { const nd = startOfISOWeek(addWeeks(d, 1)); scrollToKw(getISOWeekYear(nd), getISOWeek(nd)); return nd; }); };
  const goToday = () => { clearSelection(); const nd = startOfISOWeek(new Date()); setKwDate(nd); scrollToKw(getISOWeekYear(nd), getISOWeek(nd)); };
  const setYear = (y) => { clearSelection(); const nd = startOfISOWeek(setISOWeek(new Date(Number(y), 0, 10), activeKw)); setKwDate(nd); };

  // Export function (CSV/PDF)
  const handleExport = async (fmt) => {
    if (!selectedUserId) return;
    const from = sortedKeys[0] ? format(startOfMonth(new Date(sortedKeys[0] + "-01")), "yyyy-MM-dd") : format(startOfMonth(kwDate), "yyyy-MM-dd");
    const to = sortedKeys[sortedKeys.length - 1] ? format(endOfMonth(new Date(sortedKeys[sortedKeys.length - 1] + "-01")), "yyyy-MM-dd") : format(endOfMonth(kwDate), "yyyy-MM-dd");
    try {
      toast.info(`${fmt.toUpperCase()} Export wird erstellt...`);
      const res = await authFetch(`${API}/timesheet/export?from=${from}&to=${to}&employee_id=${selectedUserId}&format=${fmt}`);
      if (!res.ok) { toast.error("Export fehlgeschlagen"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers.get("content-disposition") || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `Stundenkonto.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} Export heruntergeladen`);
    } catch { toast.error("Export fehlgeschlagen"); }
  };

  const saldo = sm.differenz_minutes || 0;

  return (
    <div data-testid="stundenkonten-page" className="flex flex-col min-w-0" style={{ minHeight: "calc(100vh - 180px)", overflowX: "hidden" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <div className="flex items-center gap-4">
          <h2 className="text-[16px] font-bold" style={{ color: "var(--tga-text-bright)" }} data-testid="page-title">{isAdmin ? "Stundenkonten" : "Mein Stundenkonto"}</h2>
          {isAdmin && (
            <select className="h-8 text-[13px] w-56 rounded-md px-2 font-medium outline-none appearance-none cursor-pointer" style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text-primary)" }} value={selectedUserId || "__none__"} onChange={(e) => setSelectedUserId(e.target.value === "__none__" ? "" : e.target.value)} data-testid="employee-select">
              <option value="__none__">Alle Mitarbeiter</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {!hasEmployee && isAdmin && (
        <StundenkontenDashboard users={users} onSelectEmployee={(id) => setSelectedUserId(id)} />
      )}

      {hasEmployee && (
        <div className="flex flex-col">
        {/* Stats bar - unified header */}
        {cd && (
          <div className="mb-2 px-5 py-3 rounded-lg flex-shrink-0" style={{ background: "var(--tga-bg-card)", border: "1px solid var(--tga-border)" }} data-testid="month-stats-bar">
            <div className="text-[11px] font-bold mb-2.5 tracking-wider uppercase" style={{ color: "var(--tga-text-dim)" }}>{MONTHS[kwStart.getMonth()]} {kwStart.getFullYear()}</div>
            <div className="flex items-stretch gap-0 overflow-hidden">
              <StatBlock label="Arbeitstage" value={String(stats.at)} />
              {!isNachunternehmer && <StatBlock label="Soll" value={hm(sm.soll_minutes)} />}
              <StatBlock label="Ist" value={hm(sm.ist_minutes)} />
              {!isNachunternehmer && <StatBlock label="Saldo" value={`${saldo >= 0 ? "+" : ""}${hm(saldo)}`} color={saldo >= 0 ? "text-green-400" : "text-red-400"} />}
              {!isReducedView && <StatBlock label="Fahrt" value={hm(sm.fahrzeit_minutes)} />}
              {!isReducedView && <StatBlock label="KM" value={String(sm.km || 0)} />}
              {!isNachunternehmer && <StatBlock label="Urlaub" value={String(stats.urlaub)} />}
              {!isNachunternehmer && <StatBlock label="Krank" value={String(stats.krank)} />}
              <StatDivider />
              {!isNachunternehmer && vac ? (
                <>
                  <StatBlock label="Anspruch" value={
                    isEditable && !editVacation ? (
                      <span className="cursor-pointer hover:text-blue-600 transition-colors" onClick={() => { setVacInput(String(vac.annual_entitlement ?? 30)); setEditVacation(true); }} data-testid="vacation-entitlement-value">{vac.annual_entitlement ?? 30}</span>
                    ) : editVacation ? (
                      <input type="number" autoFocus min="0" max="99" className="w-12 text-center border-b-2 border-blue-400 outline-none bg-transparent font-bold text-[18px]" value={vacInput} onChange={(e) => setVacInput(e.target.value)} onBlur={() => saveVacationDays(vacInput)} onKeyDown={(e) => { if (e.key === "Enter") saveVacationDays(vacInput); if (e.key === "Escape") setEditVacation(false); }} data-testid="vacation-entitlement-input" />
                    ) : (
                      <span>{vac.annual_entitlement ?? 30}</span>
                    )
                  } testId="vacation-entitlement" />
                  <StatBlock label="Genommen" value={String(vac.approved_days ?? vac.taken_year ?? stats.urlaub ?? 0)} testId="vacation-taken" />
                  <StatBlock label="Rest" value={String(vac.remaining ?? 0)} color={(vac.remaining ?? 0) >= 0 ? "text-green-700" : "text-red-700"} testId="vacation-rest" />
                  <StatBlock label="Geplant" value={String(vac.planned_days ?? 0)} color="text-orange-600" testId="vacation-planned" />
                </>
              ) : !isNachunternehmer ? (
                <>
                  <StatBlock label="Anspruch" value="—" />
                  <StatBlock label="Genommen" value="—" />
                  <StatBlock label="Rest" value="—" />
                  <StatBlock label="Geplant" value="—" />
                </>
              ) : null}
              {!isNachunternehmer && (
                <div className="flex items-center gap-1.5 pl-3 ml-1" style={{ borderLeft: "1px solid var(--tga-border)" }}>
                  <button
                    onClick={() => setShowVacModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-md transition-colors whitespace-nowrap"
                    style={{ color: "var(--tga-green)", background: "var(--tga-green-soft)" }}
                    data-testid="plan-vacation-btn"
                  >
                    <Palmtree className="w-3.5 h-3.5" /> Urlaub planen
                  </button>
                  <button
                    onClick={() => navigate("/app/urlaub")}
                    className="relative flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-md transition-colors whitespace-nowrap border hover:bg-white/5"
                    style={{ color: "var(--tga-text-secondary)", borderColor: "var(--tga-border)" }}
                    title="Urlaubsanträge & Genehmigungen"
                    data-testid="vacation-management-btn"
                  >
                    <CalendarDays className="w-3.5 h-3.5" /> Urlaubsverwaltung
                    {pendingVacationCount > 0 && (
                      <span
                        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white"
                        style={{ background: "var(--tga-amber, #f59e0b)" }}
                        data-testid="vacation-pending-badge"
                      >
                        {pendingVacationCount}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Auto-Pause Hinweis (kompakt) */}
        {cd && (cd.auto_pause_minutes || 0) > 0 && (
          <div className="mb-2 px-3 py-1.5 rounded-lg flex items-center gap-2 flex-shrink-0" style={{ background: "var(--tga-amber-soft)", border: "1px solid var(--tga-amber)" }} data-testid="auto-pause-banner">
            <Coffee className="w-3 h-3 flex-shrink-0" style={{ color: "var(--tga-amber)" }} />
            <span className="text-[10px]" style={{ color: "var(--tga-amber)" }}>
              <strong>Auto-Pause:</strong> {cd.auto_pause_minutes} Min
              {(cd.auto_pause_min_hours || 0) > 0 && <span> (ab {cd.auto_pause_min_hours} Std.)</span>}
            </span>
          </div>
        )}

        {/* KW Navigation */}
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button onClick={goKwPrev} className="p-1.5 rounded transition-colors" style={{ color: "var(--tga-text-secondary)" }} onMouseEnter={(e) => (e.currentTarget.style.background = "var(--tga-border)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")} data-testid="kw-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-[14px] font-bold min-w-[56px] text-center" style={{ color: "var(--tga-text-bright)" }} data-testid="kw-display">KW {activeKw}</span>
            <button onClick={goKwNext} className="p-1.5 rounded transition-colors" style={{ color: "var(--tga-text-secondary)" }} onMouseEnter={(e) => (e.currentTarget.style.background = "var(--tga-border)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")} data-testid="kw-next"><ChevronRight className="w-4 h-4" /></button>
            <span className="text-[13px] ml-1" style={{ color: "var(--tga-text-dim)" }} data-testid="kw-range">{kwDateRange}</span>
            <button onClick={goToday} className="ml-3 flex items-center gap-1 px-2.5 py-1 text-[12px] font-medium rounded transition-colors" style={{ color: "#60A5FA", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)" }} data-testid="today-btn"><CalendarDays className="w-3.5 h-3.5" />Heute</button>
            <select className="ml-2 h-7 text-[12px] rounded px-1.5 appearance-none cursor-pointer outline-none" style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text-primary)" }} value={activeYear} onChange={(e) => setYear(e.target.value)} data-testid="year-select">
              {[activeYear - 1, activeYear, activeYear + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            {hasEmployee && (
              <>
                <button
                  onClick={() => handleExport("csv")}
                  className="flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium rounded transition-colors"
                  style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" }}
                  data-testid="export-csv-btn"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />CSV
                </button>
                <button
                  onClick={() => handleExport("pdf")}
                  className="flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium rounded transition-colors"
                  style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" }}
                  data-testid="export-pdf-btn"
                >
                  <FileText className="w-3.5 h-3.5" />PDF
                </button>
              </>
            )}
            {isEditable && (
              <button onClick={openResetDialog} className="flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium bg-red-600 text-white hover:bg-red-700 rounded transition-colors" data-testid="reset-account-btn"><Trash2 className="w-3.5 h-3.5" />Stundenkonto zurücksetzen</button>
            )}
            {/* Hidden-columns restore button — replaces full ColumnControls toolbar (07.02.2026 user request).
                Per-column hide is in the header eye icon, profiles/compact removed. */}
            <HiddenColumnsButton
              allColumns={COL_DEFS}
              prefs={prefs}
              isNachunternehmer={isNachunternehmer}
            />
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selDates.size > 0 && (
          <div className="mb-2 px-4 py-2.5 rounded flex items-center gap-3 flex-wrap" style={{ background: "var(--tga-green-soft)", border: "1px solid rgba(34, 197, 94, 0.3)" }} data-testid="bulk-action-bar">
            <span className="text-[13px] font-semibold" style={{ color: "var(--tga-green)" }}>{selDates.size} {selDates.size === 1 ? "Tag" : "Tage"} ausgewählt</span>
            <div className="h-4 w-px bg-green-200" />
            <div className="flex items-center gap-1.5 relative">
              <button onClick={() => setBulkAction(bulkAction === "projekt" ? null : "projekt")} className={`px-2.5 py-1 text-[12px] font-medium rounded transition-colors ${bulkAction === "projekt" ? "bg-green-600 text-white" : ""}`} style={bulkAction !== "projekt" ? { background: "var(--tga-bg-card)", border: "1px solid var(--tga-border-strong)", color: "var(--tga-green)" } : undefined} data-testid="bulk-projekt-btn">Projekt setzen</button>
              {bulkAction === "projekt" && (
                <div className="flex items-center gap-1.5">
                  <select className="h-7 text-[12px] rounded px-1.5 outline-none" value={bulkVals.proj} onChange={(e) => setBulkVals(v => ({ ...v, proj: e.target.value }))} data-testid="bulk-proj-select">
                    <option value="">Projekt wählen...</option>
                    {projects.filter(p => p.is_active !== false).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button onClick={() => applyBulk("projekt")} disabled={!bulkVals.proj || saving} className="px-2 py-1 text-[11px] font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors" data-testid="bulk-proj-apply">Anwenden</button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setBulkAction(bulkAction === "zeit" ? null : "zeit")} className={`px-2.5 py-1 text-[12px] font-medium rounded transition-colors ${bulkAction === "zeit" ? "bg-green-600 text-white" : ""}`} style={bulkAction !== "zeit" ? { background: "var(--tga-bg-card)", border: "1px solid var(--tga-border-strong)", color: "var(--tga-green)" } : undefined} data-testid="bulk-zeit-btn">Arbeitszeit setzen</button>
              {bulkAction === "zeit" && (
                <div className="flex items-center gap-1.5">
                  <input type="time" className="h-7 text-[12px] border border-green-200 rounded bg-white px-1.5 outline-none" value={bulkVals.von} onChange={(e) => setBulkVals(v => ({ ...v, von: e.target.value }))} data-testid="bulk-von" />
                  <span className="text-[11px] text-gray-400">–</span>
                  <input type="time" className="h-7 text-[12px] border border-green-200 rounded bg-white px-1.5 outline-none" value={bulkVals.bis} onChange={(e) => setBulkVals(v => ({ ...v, bis: e.target.value }))} data-testid="bulk-bis" />
                  <select className="h-7 text-[12px] border border-green-200 rounded bg-white px-1 outline-none" value={bulkVals.pause} onChange={(e) => setBulkVals(v => ({ ...v, pause: Number(e.target.value) }))} data-testid="bulk-pause">
                    {PAUSE_OPTS.map(p => <option key={p} value={p}>{p}min</option>)}
                  </select>
                  <button onClick={() => applyBulk("zeit")} disabled={saving} className="px-2 py-1 text-[11px] font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors" data-testid="bulk-zeit-apply">Anwenden</button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setBulkAction(bulkAction === "status" ? null : "status")} className={`px-2.5 py-1 text-[12px] font-medium rounded transition-colors ${bulkAction === "status" ? "bg-green-600 text-white" : ""}`} style={bulkAction !== "status" ? { background: "var(--tga-bg-card)", border: "1px solid var(--tga-border-strong)", color: "var(--tga-green)" } : undefined} data-testid="bulk-status-btn">Status setzen</button>
              {bulkAction === "status" && (
                <div className="flex items-center gap-1.5">
                  <select className="h-7 text-[12px] rounded px-1.5 outline-none" value={bulkVals.status} onChange={(e) => setBulkVals(v => ({ ...v, status: e.target.value }))} data-testid="bulk-status-select">
                    {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <button onClick={() => applyBulk("status")} disabled={saving} className="px-2 py-1 text-[11px] font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors" data-testid="bulk-status-apply">Anwenden</button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => bulkDelete()} disabled={saving} className="px-2.5 py-1 text-[12px] font-medium rounded transition-colors bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 flex items-center gap-1" data-testid="bulk-delete-btn"><Trash2 className="w-3 h-3" />Einträge löschen</button>
            </div>
            <div className="h-4 w-px bg-green-200" />
            <button onClick={clearSelection} className="px-2 py-1 text-[11px] text-gray-500 hover:text-gray-700 transition-colors" data-testid="bulk-clear">Auswahl aufheben</button>
          </div>
        )}

        {/* TABLE */}
        {isReducedView && (
          <div className="mb-2 px-3 py-1.5 rounded text-[11px] font-medium" style={{ background: "var(--tga-blue-soft)", border: "1px solid rgba(59, 130, 246, 0.3)", color: "var(--tga-blue)" }} data-testid="reduced-view-hint">
            Reduzierte Ansicht aktiv — Hinfahrt/Rückfahrt Spalten ausgeblendet
          </div>
        )}
        {loading ? (
          <div className="py-12 text-center text-[13px] text-gray-400">Lade Daten...</div>
        ) : (
          <div className="table-wrapper" style={{ width: "100%", overflow: "hidden" }}>
            <div ref={tableRef} className={`table-scroll stundenkonto-table ${prefs.compact ? "compact" : ""}`} style={{ width: "100%", background: "var(--tga-bg-card)", border: "1px solid var(--tga-border)", borderRadius: 8, maxHeight: "calc(100vh - 280px)", minHeight: 400, scrollBehavior: "auto", overflowX: "auto", overflowY: "auto" }}>
            <style>{`
              .table-wrapper { width: 100%; min-width: 0; max-width: 100%; overflow: hidden; }
              .table-scroll  { width: 100%; min-width: 0; max-width: 100%; overflow-x: auto; overflow-y: auto; }
              .stundenkonto-table table { min-width: max-content; border-collapse: separate; border-spacing: 0; }
              /* DOM-stable column toggle: hidden cols stay in DOM but don't affect layout */
              .stundenkonto-table .col-hidden { display: none !important; }
              .stundenkonto-table th .col-toggle-btn {
                opacity: 0.0; transition: opacity 0.15s ease;
                width: 16px; height: 16px; padding: 0;
                display: inline-flex; align-items: center; justify-content: center;
                background: transparent; border: 0; cursor: pointer;
                margin-left: 4px;
              }
              .stundenkonto-table th:hover .col-toggle-btn { opacity: 0.8; }
              .stundenkonto-table th .col-toggle-btn:hover { opacity: 1; background: rgba(255,255,255,0.06); border-radius: 3px; }
              .stundenkonto-table.compact td { height: 28px !important; font-size: 11px !important; }
              .stundenkonto-table.compact th { padding-top: 4px !important; padding-bottom: 4px !important; }
              .stundenkonto-table.compact select, .stundenkonto-table.compact input { font-size: 11px !important; }
              .stundenkonto-table::-webkit-scrollbar { height: 12px; width: 12px; }
              .stundenkonto-table::-webkit-scrollbar-thumb { background: var(--tga-border-strong); border-radius: 6px; }
              .stundenkonto-table::-webkit-scrollbar-thumb:hover { background: var(--tga-blue); }
            `}</style>
            <table style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: tableMinWidth, tableLayout: "fixed" }}>
              <colgroup>
                {activeCols.map(col => (
                  <col
                    key={`col-${col.key}`}
                    className={hiddenKeys.has(col.key) ? "col-hidden" : ""}
                    style={{ width: getColW(col.key) }}
                  />
                ))}
              </colgroup>
              <thead style={{ position: "sticky", top: 0, zIndex: 20 }}>
                {/* Gruppierungszeile */}
                <tr style={{ backgroundColor: "var(--tga-bg-elevated)" }}>
                  <th colSpan={4} style={{ borderRight: "2px solid var(--tga-border-strong)", position: "sticky", left: 0, zIndex: 25, backgroundColor: "var(--tga-bg-elevated)" }} />
                  <th style={{ borderRight: "2px solid var(--tga-border-strong)" }} />
                  {showTravel && <th colSpan={3} className="text-center text-[9px] font-bold py-1 tracking-wide" style={{ borderRight: "2px solid var(--tga-border-strong)", color: "var(--tga-text-dim)" }}>Hinfahrt</th>}
                  <th colSpan={4} className="text-center text-[9px] font-bold py-1 tracking-wide" style={{ borderRight: "2px solid var(--tga-border-strong)", color: "var(--tga-text-dim)" }}>Arbeitszeit</th>
                  {showTravel && <th colSpan={3} className="text-center text-[9px] font-bold py-1 tracking-wide" style={{ borderRight: "2px solid var(--tga-border-strong)", color: "var(--tga-text-dim)" }}>Rückfahrt</th>}
                  <th colSpan={3} />
                  <th colSpan={4} className="text-center text-[9px] font-bold py-1 tracking-wide" style={{ color: "var(--tga-text-dim)" }}>Abrechnung</th>
                </tr>
                {/* Spaltenüberschriften */}
                <tr style={{ backgroundColor: "var(--tga-bg-elevated)", borderBottom: "2px solid var(--tga-border-strong)", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }}>
                  {activeCols.map((col, ci) => (
                    <th key={col.key} className={TH} style={{ ...(ci < activeCols.length - 1 ? (col.ge ? thGroupBorder : thBorder) : {}), width: getColW(col.key), minWidth: getColW(col.key), position: "relative", ...getStickyHeaderStyle(ci) }} data-testid={`th-${col.key}`}>
                      <span className="inline-flex items-center gap-0.5">
                        {col.label}
                        {!LOCKED_KEYS.has(col.key) && (
                          <button
                            type="button"
                            className="col-toggle-btn"
                            onClick={(e) => { e.stopPropagation(); prefs.hideColumn(col.key); }}
                            title="Spalte ausblenden"
                            data-testid={`th-hide-${col.key}`}
                          >
                            <EyeOff className="w-3 h-3" style={{ color: "var(--tga-text-dim)" }} />
                          </button>
                        )}
                      </span>
                      {ci < activeCols.length - 1 && (
                        <div
                          onMouseDown={(e) => onResizeStart(e, col.key)}
                          style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 5, cursor: "col-resize", zIndex: 2 }}
                          className="hover:bg-blue-400 active:bg-blue-500 transition-colors"
                          data-testid={`resize-${col.key}`}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  if (row.type === "kw") return (
                    <tr key={`kw-${row.kwK}`} data-kw={row.kwK} style={{ backgroundColor: "var(--tga-bg-elevated)", borderTop: "3px solid var(--tga-border-strong)" }} data-testid={`kw-header-${row.kw}`}>
                      <td colSpan={COL_COUNT + 1} className="px-4 py-2 text-[12px] font-extrabold tracking-wide" style={{ color: "var(--tga-text-secondary)" }}>{row.label}</td>
                    </tr>
                  );
                  if (row.type === "wk-sum") {
                    const diff = row.wIst - row.wSoll;
                    return (
                      <tr key={`ws-${row.kwK}`} style={{ backgroundColor: "var(--tga-bg-elevated)", borderTop: "2px solid var(--tga-border-strong)" }} data-testid={`kw-sum-${row.kw}`}>
                        <td colSpan={4} style={thGroupBorder} />
                        <td className="px-4 py-1.5 text-[11px] font-bold text-right" style={{ ...thGroupBorder, color: "var(--tga-text-muted)" }}>Σ Woche</td>
                        {showTravel && <td colSpan={3} style={thGroupBorder} />}
                        <td colSpan={3} style={{ borderRight: "1px solid var(--tga-border)" }} />
                        <td className="px-2 py-1.5 text-[12px] font-bold" style={{ ...thGroupBorder, color: "#60A5FA" }}>{hm(row.wIst)}</td>
                        {showTravel && <>
                          <td colSpan={2} className="px-2 py-1.5 text-[11px] font-medium" style={{ borderRight: "1px solid var(--tga-border)", color: "#A78BFA" }}>{hm(row.wFahrt)}</td>
                          <td style={thGroupBorder} />
                        </>}
                        <td className="px-2 py-1.5 text-[12px] font-bold" style={{ borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-muted)" }}>{hm(row.wSoll)}</td>
                        <td className="px-2 py-1.5 text-[12px] font-bold" style={{ borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-primary)" }}>{hm(row.wIst)}</td>
                        <td className="px-2 py-1.5 text-[12px] font-bold" style={{ color: diff >= 0 ? "#34D399" : "#F87171" }}>{diff >= 0 ? "+" : "-"}{hm(diff)}</td>
                        {/* Abrechnungs-Spalten leer fuer Wochensumme */}
                        <td colSpan={4} style={{ borderLeft: "2px solid var(--tga-border-strong)" }} />
                      </tr>
                    );
                  }
                  if (row.type === "dy-sum") {
                    const diff = row.dAz - row.dSoll;
                    return (
                      <tr key={`ds-${row.date}`} style={{ backgroundColor: "var(--tga-bg-elevated)", borderBottom: "1px solid var(--tga-border)" }}>
                        <td colSpan={4} style={thGroupBorder} />
                        <td className="px-4 py-1 text-[10px] font-bold text-right" style={{ ...thGroupBorder, color: "var(--tga-text-muted)" }}>Σ Tag</td>
                        {showTravel && <td colSpan={3} style={thGroupBorder} />}
                        <td colSpan={3} style={{ borderRight: "1px solid var(--tga-border)" }} />
                        <td className="px-2 py-1 text-[11px] font-bold" style={{ ...thGroupBorder, color: "var(--tga-blue)" }}>{hm(row.dAz)}</td>
                        {showTravel && <>
                          <td colSpan={2} className="px-2 py-1 text-[11px] font-medium" style={{ borderRight: "1px solid var(--tga-border)", color: "var(--tga-purple)" }}>{hm(row.dFahrt)}</td>
                          <td style={thGroupBorder} />
                        </>}
                        <td className="px-2 py-1 text-[11px] font-bold" style={{ borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-muted)" }}>{hm(row.dSoll)}</td>
                        <td className="px-2 py-1 text-[11px] font-bold" style={{ borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-primary)" }}>{hm(row.dAz)}</td>
                        <td className="px-2 py-1 text-[11px] font-bold" style={{ color: diff >= 0 ? "var(--tga-green)" : "var(--tga-red)" }}>{diff >= 0 ? "+" : "-"}{hm(diff)}</td>
                        <td colSpan={4} style={{ borderLeft: "2px solid var(--tga-border-strong)" }} />
                      </tr>
                    );
                  }

                  /* Data row */
                  const isT = row.date === TODAY;
                  const isEd = editId === row.eId && row.eId;
                  const ef = isEd ? editField : null;
                  const isSel = selDates.has(row.date);
                  const isFlash = flashId && flashId === row.eId;
                  const isStatusGray = row.isWe || row.isHol || (row.absT && row.absT !== "arbeitstag" && row.absT !== "weekend" && row.absT !== "urlaub");
                  const isVacApproved = row.absT === "urlaub" && row.vacStatus !== "geplant";
                  const isVacPlanned = row.absT === "urlaub" && row.vacStatus === "geplant";
                  const isBilled = !!row.abger;
                  // BUGFIX 07.02.2026: sticky-Spalten waren transparent. Jetzt deckende Zeilen-Hintergrundfarben.
                  // Vorher: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)" — beide nicht deckend
                  // Jetzt: solide Card-Color (alternierend mit Zebra-Stripe) damit Inhalte unter sticky cols nicht durchscheinen.
                  const baseRowBg = i % 2 === 0 ? "var(--tga-bg-card)" : "var(--tga-bg-card-alt)";
                  const bg = isSel ? "var(--tga-green-soft)" : isFlash ? "var(--tga-blue-soft)" : isEd ? "var(--tga-blue-soft)" : isVacApproved ? "var(--tga-green-soft)" : isVacPlanned ? "var(--tga-amber-soft)" : isBilled ? "rgba(34, 197, 94, 0.08)" : isStatusGray ? "var(--tga-bg-elevated)" : isT ? "var(--tga-blue-soft)" : baseRowBg;
                  const hoverBg = isSel ? "var(--tga-green-soft)" : isFlash ? "var(--tga-blue-soft)" : isEd ? "var(--tga-blue-soft)" : isVacApproved ? "var(--tga-green-soft)" : isVacPlanned ? "var(--tga-amber-soft)" : isStatusGray ? "var(--tga-bg-hover)" : "var(--tga-bg-hover)";
                  const cellClick = (field) => row.eId && isEditable ? () => { clearSelAndEdit(() => startCellEdit(row, field)); } : selDates.size ? () => clearSelection() : undefined;
                  const cellCur = row.eId && isEditable ? "cursor-pointer" : selDates.size ? "cursor-pointer" : "";
                  const cellHl = (field) => ef === field ? "ring-1 ring-blue-400/30 ring-inset rounded-sm" : "";
                  const canSelect = row.first && !row.isWe && !row.isHol && row.absT !== "feiertag" && isEditable;
                  const projAllowed = row.absT === "arbeitstag" || row.absT === "feiertag" || row.absT === "urlaub" || row.absT === "krank";
                  const grundLabel = (v) => GRUND_OPTS.find(g => g.value === v)?.label || v || "–";

                  return (
                    <tr key={row.eId || `d-${row.date}-${i}`} id={row.first ? `row-${row.date}` : undefined}
                      className="group"
                      style={{ backgroundColor: bg, borderBottom: "1px solid var(--tga-border)", transition: "background-color 0.25s ease", borderLeft: isSel && row.first ? "3px solid var(--tga-green)" : isT && row.first ? "3px solid var(--tga-blue)" : isEd ? "3px solid var(--tga-blue)" : undefined }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = bg; }}
                      data-testid={row.eId ? `entry-${row.eId}` : `day-${row.date}`}
                    >
                      {/* WT */}
                      <td className={`px-2 text-[12px] font-medium ${canSelect ? "cursor-pointer select-none" : ""}`} style={{ ...tdS, ...getStickyStyle(0), color: isSel ? "var(--tga-green)" : "var(--tga-text-muted)", fontWeight: isSel ? 700 : undefined, backgroundColor: bg }} onClick={canSelect ? () => handleRowSelect(row.date) : undefined} data-testid={`wt-${row.date}`}>
                        <span className="flex items-center gap-1">
                          {row.wd}
                          {row.first && row.clockInLoc && (
                            <span className="relative group" data-testid={`gps-${row.date}`}>
                              <button
                                onClick={(e) => { e.stopPropagation(); setGpsMapData({ clockInLoc: row.clockInLoc, clockOutLoc: row.clockOutLoc, locStatus: row.locStatus }); }}
                                className="inline-flex"
                                data-testid={`gps-btn-${row.date}`}
                              >
                                <MapPin className={`w-3 h-3 ${row.locStatus === "vor_ort" ? "text-green-500" : row.locStatus === "ausserhalb" ? "text-orange-500" : "text-blue-400"} hover:scale-125 transition-transform`} />
                              </button>
                              <span className="absolute left-0 top-full z-50 hidden group-hover:block bg-gray-900 text-white text-[10px] rounded px-2 py-1.5 whitespace-nowrap shadow-lg mt-1" style={{ minWidth: 180 }}>
                                {row.clockInLoc?.address && <span className="block">Start: {row.clockInLoc.address.substring(0, 50)}</span>}
                                {row.clockOutLoc?.address && <span className="block">Ende: {row.clockOutLoc.address.substring(0, 50)}</span>}
                                {row.locStatus && <span className={`block mt-0.5 font-medium ${row.locStatus === "vor_ort" ? "text-green-300" : "text-orange-300"}`}>{row.locStatus === "vor_ort" ? "Vor Ort" : "Ausserhalb"}</span>}
                                <span className="block mt-0.5 text-gray-400">Klick: Karte anzeigen</span>
                              </span>
                            </span>
                          )}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="px-0.5" style={{ ...tdS, ...getStickyStyle(1), backgroundColor: bg }}>
                        {isEditable && row.eId && (
                          <div className="flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => delEntry(row.eId, row.date)} className="p-0.5" style={{ color: "var(--tga-text-dim)" }} data-testid={`del-${row.eId}`}><Trash2 className="w-3 h-3 hover:text-red-400" /></button>
                            {row.first && projAllowed && <button onClick={() => addEntry(row.date)} className="p-0.5" style={{ color: "var(--tga-text-dim)" }} title="Weiteren Eintrag hinzufügen" data-testid={`add-more-${row.date}`}><Plus className="w-3 h-3 hover:text-blue-400" /></button>}
                          </div>
                        )}
                        {isEditable && !row.eId && row.first && !row.isWe && !row.isHol && (
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => addEntry(row.date)} className="p-0.5" style={{ color: "var(--tga-text-dim)" }} data-testid={`add-btn-${row.date}`}><Plus className="w-3 h-3 hover:text-blue-400" /></button>
                            {row.prev && <button onClick={() => copyPrevDay(row)} className="p-0.5" style={{ color: "var(--tga-text-dim)" }} title="Vortag übernehmen" data-testid={`copy-prev-${row.date}`}><CopyPlus className="w-3 h-3 hover:text-amber-400" /></button>}
                          </div>
                        )}
                      </td>

                      {/* Datum */}
                      <td className="px-2 text-[12px] font-medium" style={{ ...tdS, ...getStickyStyle(2), backgroundColor: bg, color: "var(--tga-text-bright)" }}>{row.ds}</td>

                      {/* Status */}
                      <td className="px-2 text-[12px]" style={{ ...tdGS, color: (row.isHol || row.absT === "feiertag") ? "var(--tga-red)" : row.absT === "urlaub" && row.vacStatus !== "geplant" ? "var(--tga-green)" : row.absT === "urlaub" && row.vacStatus === "geplant" ? "var(--tga-amber)" : "var(--tga-text-secondary)" }}>
                        {row.first && isEditable ? (
                          <select className={plainSelect} value={row.absT} onChange={(e) => updateStatus(row.date, e.target.value)} data-testid={`status-${row.date}`} style={{ color: row.absT === "feiertag" ? "var(--tga-red)" : row.absT === "urlaub" && row.vacStatus !== "geplant" ? "var(--tga-green)" : row.absT === "urlaub" && row.vacStatus === "geplant" ? "var(--tga-amber)" : row.absT === "frei" || row.absT === "weekend" ? "var(--tga-text-muted)" : "var(--tga-text-secondary)" }}>
                            {row.isWe && <option value="weekend" style={{ color: "var(--tga-text-muted)" }}>Wochenende</option>}
                            {STATUS_OPTS.map(s => <option key={s.value} value={s.value} style={{ color: s.value === "feiertag" ? "var(--tga-red)" : s.value === "frei" ? "var(--tga-text-muted)" : "var(--tga-text-secondary)" }}>{s.label}</option>)}
                          </select>
                        ) : <span className={(row.isHol || row.absT === "feiertag") ? "font-medium" : row.absT === "urlaub" ? "font-medium" : ""}>{row.sLbl}</span>}
                      </td>

                      {/* Projekt – Feiertag: Freitext, sonst Projekt-Picker */}
                      <td
                        className={`px-2 text-[12px] relative ${projAllowed && isEditable ? "cursor-pointer transition-colors" : !projAllowed ? "cursor-not-allowed" : ""}`}
                        style={{ ...tdGS, ...(projAllowed ? {} : { opacity: 0.5 }), color: "var(--tga-text-primary)" }}
                        onClick={() => {
                          if (!projAllowed || !isEditable) return;
                          if (selDates.size) { clearSelection(); return; }
                          if (row.absT === "feiertag" || row.absT === "urlaub" || row.absT === "krank") {
                            // Feiertag/Urlaub/Krank: open free-text edit
                            if (row.eId) { startCellEdit(row, "projText"); }
                            else if (row.first && !row.isWe) { addEntry(row.date, false); }
                            return;
                          }
                          // Normal: open project picker
                          if (Date.now() - projCloseRef.current < 150) return;
                          if (row.eId) {
                            setProjPickerId(projPickerId === row.eId ? null : row.eId);
                          } else if (row.first && !row.isWe) {
                            addEntry(row.date, true);
                          }
                        }}
                        data-testid={`proj-cell-${row.date}${row.eId ? `-${row.eId}` : ""}`}
                      >
                        <div className="relative">
                          {ef === "projText" ? (
                            <input type="text" autoFocus className="w-full h-full bg-transparent text-[12px] border-none outline-none px-0 font-medium" style={{ color: "var(--tga-red)" }} value={ev.projText} onChange={(e) => setEv(v => ({ ...v, projText: e.target.value }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`projtext-${row.eId}`} />
                          ) : (
                            <span className={`truncate flex items-center gap-1.5 ${projAllowed && isEditable && row.eId ? "hover:text-blue-400" : ""} ${(row.absT === "feiertag" || row.absT === "urlaub" || row.absT === "krank") && row.proj ? "font-medium" : ""}`} style={(row.absT === "feiertag" || row.absT === "urlaub" || row.absT === "krank") && row.proj ? { color: "var(--tga-red)" } : undefined} data-testid={row.eId ? `proj-click-${row.eId}` : undefined}>
                              {row.pId && (() => { const pp = projects.find(p => p.id === row.pId); return pp ? <span className="inline-block w-2 h-2 shrink-0 rounded-[1px]" style={{ backgroundColor: pp.color || getProjectTypeColor(pp.project_type) }} /> : null; })()}
                              {!projAllowed ? <span className="italic" style={{ color: "var(--tga-text-dim)" }}>—</span>
                                : row.eId ? (isEd && row.absT !== "feiertag" && row.absT !== "urlaub" && row.absT !== "krank" && ev.pId !== undefined ? (projects.find(p => p.id === ev.pId)?.name || row.proj || "– Projekt wählen –") : (row.proj || ((row.absT === "feiertag" || row.absT === "urlaub" || row.absT === "krank") ? "– Text eingeben –" : "– Projekt wählen –")))
                                : row.proj ? <span className="font-medium" style={{ color: "var(--tga-red)" }}>{row.proj}</span> : <span style={{ color: "var(--tga-text-dim)" }}>{(row.absT === "feiertag" || row.absT === "urlaub" || row.absT === "krank") ? "– Text eingeben –" : "– Projekt wählen –"}</span>}
                            </span>
                          )}
                          {projPickerId === row.eId && row.eId && row.absT !== "feiertag" && row.absT !== "urlaub" && row.absT !== "krank" && (
                            <InlineProjectPicker projects={projects} currentId={isEd ? ev.pId : row.pId} recentIds={recentProjectIds}
                              onSelect={(pId) => { if (isEd) { setEv(v => ({ ...v, pId })); setProjPickerId(null); } else { setProjectDirect(row.eId, pId, row.date); } }}
                              onClose={() => { projCloseRef.current = Date.now(); setProjPickerId(null); }} />
                          )}
                        </div>
                      </td>

                      {/* Hinfahrt KM */}
                      {showTravel && <td className={`px-1 text-[12px] ${cellCur} ${cellHl("kmH")}`} style={{ ...tdS, color: "var(--tga-text-secondary)" }} onClick={cellClick("kmH")}>
                        {ef === "kmH" ? <input type="number" autoFocus className="w-full h-full bg-transparent text-[11px] border-none outline-none px-0 text-right" value={ev.kmH} onChange={(e) => setEv(v => ({ ...v, kmH: e.target.value }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`kmh-${row.eId}`} />
                        : row.kmH || "–"}
                      </td>}

                      {/* Hinfahrt Zeit */}
                      {showTravel && <td className={`px-1 text-[12px] ${cellCur} ${cellHl("fzH")}`} style={{ ...tdS, color: "var(--tga-text-secondary)" }} onClick={cellClick("fzH")}>
                        {ef === "fzH" ? <input type="number" autoFocus className="w-full h-full bg-transparent text-[11px] border-none outline-none px-0 text-right" placeholder="min" value={ev.fzH} onChange={(e) => setEv(v => ({ ...v, fzH: e.target.value }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`fzh-${row.eId}`} />
                        : row.fzH ? hm(row.fzH) : "–"}
                      </td>}

                      {/* Grund Hinfahrt */}
                      {showTravel && <td className={`px-1 text-[11px] ${cellCur} ${cellHl("gH")}`} style={{ ...tdGS, color: "var(--tga-text-secondary)" }} onClick={cellClick("gH")}>
                        {ef === "gH" ? (
                          <div>
                            <select autoFocus className={plainSelect + " text-[11px]"} value={ev.gH} onChange={(e) => setEv(v => ({ ...v, gH: e.target.value }))} onKeyDown={(e) => { if (e.key === "Escape") cancelCellEdit(); }} onBlur={() => { if (ev.gH !== "sonstiges") saveCellEdit(row); }} data-testid={`gh-${row.eId}`}>
                              {GRUND_OPTS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                            </select>
                            {ev.gH === "sonstiges" && <input autoFocus className="w-full text-[10px] border border-gray-200 rounded mt-0.5 px-1 outline-none" placeholder="Grund..." value={ev.gHN || ""} onChange={(e) => setEv(v => ({ ...v, gHN: e.target.value }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`ghn-${row.eId}`} />}
                          </div>
                        ) : <span className="truncate block">{grundLabel(row.gH)}</span>}
                      </td>}

                      {/* Von */}
                      <td className={`px-1 text-[12px] ${cellCur} ${cellHl("von")}`} style={{ ...tdS, color: "var(--tga-text-primary)" }} onClick={cellClick("von")}>
                        {ef === "von" ? <input type="time" autoFocus className="w-full h-full bg-transparent text-[11px] border-none outline-none px-0" value={ev.von} onChange={(e) => setEv(v => ({ ...v, von: e.target.value }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`von-${row.eId}`} />
                        : row.von || "–"}
                      </td>

                      {/* Bis */}
                      <td className={`px-1 text-[12px] ${cellCur} ${cellHl("bis")}`} style={{ ...tdS, color: "var(--tga-text-primary)" }} onClick={cellClick("bis")}>
                        {ef === "bis" ? <input type="time" autoFocus className="w-full h-full bg-transparent text-[11px] border-none outline-none px-0" value={ev.bis} onChange={(e) => setEv(v => ({ ...v, bis: e.target.value }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`bis-${row.eId}`} />
                        : row.bis || "–"}
                      </td>

                      {/* Pause */}
                      <td className={`px-1 text-[12px] ${cellCur} ${cellHl("pause")}`} style={{ ...tdS, color: "var(--tga-text-secondary)" }} onClick={cellClick("pause")}>
                        {ef === "pause" ? <select autoFocus className={plainSelect + " text-[11px]"} value={ev.pause} onChange={(e) => setEv(v => ({ ...v, pause: Number(e.target.value) }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`pause-${row.eId}`}>{PAUSE_OPTS.map(p => <option key={p} value={p}>{p}m</option>)}</select>
                        : <span>{row.pause}m{row.first && row.autoPauseApplied && <span className="text-amber-600 text-[9px] ml-0.5" title={`Automatische Pause: ${row.autoPauseMinutes} Min.`}>A</span>}</span>}
                      </td>

                      {/* Arbeit (calculated) */}
                      <td className="px-2 text-[12px] font-medium" style={{ ...tdGS, color: "var(--tga-text-primary)" }}>
                        {isEd ? hm(Math.max(0, toM(ev.bis ?? row.bis) - toM(ev.von ?? row.von) - (ev.pause ?? row.pause))) : hm(row.az)}
                      </td>

                      {/* F.Rück KM */}
                      {showTravel && <td className={`px-1 text-[12px] ${cellCur} ${cellHl("kmR")}`} style={{ ...tdS, color: "var(--tga-text-secondary)" }} onClick={cellClick("kmR")}>
                        {ef === "kmR" ? <input type="number" autoFocus className="w-full h-full bg-transparent text-[11px] border-none outline-none px-0 text-right" value={ev.kmR} onChange={(e) => setEv(v => ({ ...v, kmR: e.target.value }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`kmr-${row.eId}`} />
                        : row.kmR || "–"}
                      </td>}

                      {/* F.Rück Z (minutes) */}
                      {showTravel && <td className={`px-1 text-[12px] ${cellCur} ${cellHl("fzR")}`} style={{ ...tdS, color: "var(--tga-text-secondary)" }} onClick={cellClick("fzR")}>
                        {ef === "fzR" ? <input type="number" autoFocus className="w-full h-full bg-transparent text-[11px] border-none outline-none px-0 text-right" placeholder="min" value={ev.fzR} onChange={(e) => setEv(v => ({ ...v, fzR: e.target.value }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`fzr-${row.eId}`} />
                        : row.fzR ? hm(row.fzR) : "–"}
                      </td>}

                      {/* Grund Rück */}
                      {showTravel && <td className={`px-1 text-[11px] ${cellCur} ${cellHl("gR")}`} style={{ ...tdGS, color: "var(--tga-text-secondary)" }} onClick={cellClick("gR")}>
                        {ef === "gR" ? (
                          <div>
                            <select autoFocus className={plainSelect + " text-[11px]"} value={ev.gR} onChange={(e) => setEv(v => ({ ...v, gR: e.target.value }))} onKeyDown={(e) => { if (e.key === "Escape") cancelCellEdit(); }} onBlur={() => { if (ev.gR !== "sonstiges") saveCellEdit(row); }} data-testid={`gr-${row.eId}`}>
                              {GRUND_OPTS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                            </select>
                            {ev.gR === "sonstiges" && <input autoFocus className="w-full text-[10px] border border-gray-200 rounded mt-0.5 px-1 outline-none" placeholder="Grund..." value={ev.gRN || ""} onChange={(e) => setEv(v => ({ ...v, gRN: e.target.value }))} onKeyDown={(e) => onCellKey(e, row)} onBlur={() => saveCellEdit(row)} data-testid={`grn-${row.eId}`} />}
                          </div>
                        ) : <span className="truncate block">{grundLabel(row.gR)}</span>}
                      </td>}

                      {/* Soll */}
                      <td className="px-2 text-[12px]" style={{ ...tdS, color: "var(--tga-text-muted)" }}>{row.first && row.soll ? hm(row.soll) : ""}</td>

                      {/* Ist */}
                      <td className="px-2 text-[12px] font-medium" style={{ ...tdS, color: "var(--tga-text-primary)" }}>
                        {row.first ? hm(row.isAutoIst ? row.dayIst : (() => { const dRows = rows.filter(r => r.type === "entry" && r.date === row.date); return dRows.reduce((s, r) => s + r.az, 0); })()) : ""}
                      </td>

                      {/* +/- */}
                      <td className="px-2 text-[12px] font-bold" style={tdGS}>
                        {row.first ? (() => {
                          const ist = row.isAutoIst ? row.dayIst : rows.filter(r => r.type === "entry" && r.date === row.date).reduce((s, r) => s + r.az, 0);
                          const diff = ist - row.soll;
                          if (!row.soll && !ist) return "";
                          return <span style={{ color: diff >= 0 ? "var(--tga-green)" : "var(--tga-red)" }}>{diff >= 0 ? "+" : "-"}{hm(diff)}</span>;
                        })() : ""}
                      </td>

                      {/* VMA (Verpflegungsmehraufwand) */}
                      <td className={`px-2 text-[12px] ${row.eId && isEditable ? "cursor-pointer" : ""}`}
                          style={{ ...tdS, color: "var(--tga-text-secondary)" }}
                          onClick={() => row.eId && isEditable && startCellEdit(row, "vma")}
                          data-testid={row.eId ? `vma-cell-${row.eId}` : undefined}>
                        {ef === "vma" ? (
                          <input type="number" autoFocus min="0" step="0.5"
                            className="w-full h-full bg-transparent text-[12px] border-none outline-none px-0"
                            value={ev.vma ?? row.vma ?? 0}
                            onChange={(e) => setEv(v => ({ ...v, vma: e.target.value }))}
                            onBlur={async () => {
                              const v = parseFloat(ev.vma);
                              if (!isNaN(v) && v !== row.vma) {
                                await quickPatchEntry(row.eId, row.date, "meal_allowance", v, "meal_allowance");
                              }
                              cancelCellEdit();
                            }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur(); }}
                            data-testid={`vma-input-${row.eId}`} />
                        ) : (row.vma > 0 ? `${row.vma.toFixed(0)}€` : "")}
                      </td>

                      {/* Übernachtung */}
                      <td className="px-2 text-center"
                          style={{ ...tdS }}
                          data-testid={row.eId ? `nacht-cell-${row.eId}` : undefined}>
                        {row.eId && (
                          <input type="checkbox" checked={!!row.nacht}
                            disabled={!isEditable}
                            onChange={(e) => quickPatchEntry(row.eId, row.date, "overnight_stay", e.target.checked, "overnight_stay")}
                            className="w-3.5 h-3.5 cursor-pointer"
                            style={{ accentColor: "var(--tga-blue)" }}
                            data-testid={`nacht-cb-${row.eId}`} />
                        )}
                      </td>

                      {/* Unterkunft */}
                      <td className={`px-2 text-[11px] ${row.eId && isEditable ? "cursor-pointer" : ""}`}
                          style={{ ...tdS, color: "var(--tga-text-secondary)" }}
                          onClick={() => row.eId && isEditable && startCellEdit(row, "unterkunft")}
                          title={row.unterkunft || ""}
                          data-testid={row.eId ? `unterkunft-cell-${row.eId}` : undefined}>
                        {ef === "unterkunft" ? (
                          <input type="text" autoFocus
                            className="w-full h-full bg-transparent text-[11px] border-none outline-none px-0"
                            placeholder="Hotel/Adresse"
                            value={ev.unterkunft ?? row.unterkunft ?? ""}
                            onChange={(e) => setEv(v => ({ ...v, unterkunft: e.target.value }))}
                            onBlur={async () => {
                              const v = (ev.unterkunft ?? "").trim();
                              if (v !== (row.unterkunft || "")) {
                                await quickPatchEntry(row.eId, row.date, "accommodation_location", v, "accommodation_location");
                              }
                              cancelCellEdit();
                            }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur(); }}
                            data-testid={`unterkunft-input-${row.eId}`} />
                        ) : (
                          <span className="truncate block">{row.unterkunft || ""}</span>
                        )}
                      </td>

                      {/* Abgerechnet */}
                      <td className="px-2 text-center"
                          style={{ ...tdS }}
                          data-testid={row.eId ? `abger-cell-${row.eId}` : undefined}>
                        {row.eId && (
                          <input type="checkbox" checked={!!row.abger}
                            disabled={!isEditable}
                            onChange={(e) => quickPatchEntry(row.eId, row.date, "is_billed", e.target.checked, "is_billed")}
                            className="w-3.5 h-3.5 cursor-pointer"
                            style={{ accentColor: "var(--tga-green)" }}
                            data-testid={`abger-cb-${row.eId}`} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div ref={bottomRef} className="h-8" />
            </div>
          </div>
        )}
      </div>)}

      {/* Reset Account Dialog */}
      {showResetDialog && (
        <ResetDialog
          resetFrom={resetFrom}
          setResetFrom={setResetFrom}
          resetTo={resetTo}
          setResetTo={setResetTo}
          onClose={() => setShowResetDialog(false)}
          onConfirm={resetAccount}
          saving={saving}
        />
      )}

      {/* Vacation Planning Modal */}
      <VacationPlanModal
        open={showVacModal}
        onOpenChange={setShowVacModal}
        selectedUserId={selectedUserId}
        activeYear={activeYear}
        onSuccess={async () => {
          await loadFullYear(activeYear);
          const vacRes = await authFetch(`${API}/vacation/extended-summary/${selectedUserId}/${activeYear}`);
          if (vacRes.ok) setVacSummary(await vacRes.json());
        }}
      />

      {/* GPS Mini-Map Modal */}
      {gpsMapData && (
        <GpsMiniMap
          clockInLoc={gpsMapData.clockInLoc}
          clockOutLoc={gpsMapData.clockOutLoc}
          locStatus={gpsMapData.locStatus}
          onClose={() => setGpsMapData(null)}
        />
      )}
    </div>
  );
}

function StatBlock({ label, value, color, testId }) {
  return (
    <div className="flex-1 min-w-0 text-center px-2" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wider leading-none mb-1 whitespace-nowrap" style={{ color: "var(--tga-text-dim)" }}>{label}</div>
      <div className={`text-[18px] font-bold leading-none whitespace-nowrap ${color || ""}`} style={color ? undefined : { color: "var(--tga-text-primary)" }}>
        {value}
      </div>
    </div>
  );
}

function StatDivider() {
  return <div className="w-px mx-1 self-stretch flex-shrink-0" style={{ background: "var(--tga-border-strong)" }} />;
}
